// src/pages/Dashboard/index.js - ATUALIZADO COM LÓGICA DE STREAMING

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ========== IMPORTS DE HOOKS (PRIMEIRO) ==========
import { useDashboardCases } from './hooks/useDashboardCases';
import { useDashboardJurisprudence } from './hooks/useDashboardJurisprudence';
import { useDashboardChat } from './hooks/useDashboardChat';
import { useDashboardDocuments } from './hooks/useDashboardDocuments';

// ========== IMPORTS DE COMPONENTES (DEPOIS) ==========
import DashboardHeader from './components/Header/DashboardHeader';
import CaseSidebar from './components/Sidebar/CaseSidebar';
import CasePanel from './components/CasePanel/CasePanel';
import ChatPanel from './components/ChatPanel/ChatPanel';

// ========== IMPORTS DE DIALOGS (POR ÚLTIMO) ==========
import NewCaseDialog from './components/Dialogs/NewCaseDialog';
import JurisprudenceDetailsDialog from './components/Dialogs/JurisprudenceDetailsDialog';
import GenerateDocumentDialog from './components/Dialogs/GenerateDocumentDialog';

// ========== ESTILOS ==========
import './Dashboard.css';

/**
 * Dashboard Principal - Orquestrador de todos os componentes
 * Gerencia estado local de UI e integra hooks de negócio
 */
const Dashboard = () => {
  const navigate = useNavigate();

  // ========== ESTADO LOCAL DE UI ==========
  const [showMenuSanduiche, setShowMenuSanduiche] = useState(false);
  const [showNovoCasoDialog, setShowNovoCasoDialog] = useState(false);
  const [showJurisprudenciaDetalhes, setShowJurisprudenciaDetalhes] = useState(false);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  // ✅ NOVO: Estado para controlar se o modo streaming está ativo (pode virar um switch no UI)
  const [isStreamingEnabled] = useState(true);

  // ========== HOOKS DE NEGÓCIO ==========
  
  // Hook de Casos
  const {
    casos,
    casoSelecionado,
    loadingCasos,
    criandoCaso,
    selecionarCaso,
    criarNovoCaso
  } = useDashboardCases();

  // Hook de Jurisprudências (depende do caso selecionado)
  const {
    jurisprudenciasRelacionadas,
    loadingJurisprudencias,
    jurisprudenciaSelecionada,
    removerJurisprudencia,
    selecionarJurisprudencia,
    limparJurisprudenciaSelecionada
  } = useDashboardJurisprudence(casoSelecionado?.id);

  // Hook de Chat (depende do caso e jurisprudências)
  const {
    models,
    selectedModel,
    messages,
    inputMessage,
    isLoading: isChatLoading,
    messagesEndRef,
    sendMessage,
    sendStreamMessage, // ✅ NOVO: Obtém a função de streaming do hook
    selectModel,
    updateInputMessage
  } = useDashboardChat(casoSelecionado, jurisprudenciasRelacionadas);

  // Hook de Documentos (depende do caso)
  const {
    documentoSelecionado,
    instrucoesAdicionais,
    conteudoGerado,
    conteudoGeradoDisponivel,
    gerandoCorpoDocumento,
    baixandoDocumento,
    selecionarDocumento,
    limparDocumentoSelecionado,
    atualizarInstrucoesAdicionais,
    gerarCorpoDocumento,
    copiarConteudo,
    downloadDocx
  } = useDashboardDocuments(casoSelecionado);

  // ========== HANDLERS DE NAVEGAÇÃO ==========
  
  const handleBackToSystem = () => {
    navigate('/gestao-casos');
  };

  const handleToggleSidebar = () => {
    setShowMenuSanduiche(!showMenuSanduiche);
  };

  const handleToggleDebug = () => {
    setDebugMode(!debugMode);
  };

  // ========== HANDLERS DE CASOS ==========

  const handleSelectCaso = (caso) => {
    selecionarCaso(caso);
    setShowMenuSanduiche(false);
  };

  const handleOpenNewCaseDialog = () => {
    setShowNovoCasoDialog(true);
    setShowMenuSanduiche(false);
  };

  const handleCloseNewCaseDialog = () => {
    setShowNovoCasoDialog(false);
  };

  const handleCreateCase = async (dadosCaso) => {
    try {
      const success = await criarNovoCaso(dadosCaso);
      if (success) {
        alert('Caso criado com sucesso!');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao criar caso:', error);
      throw error;
    }
  };

  // ========== HANDLERS DE JURISPRUDÊNCIAS ==========

  const handleViewJurisprudenciaDetails = (jurisprudencia) => {
    selecionarJurisprudencia(jurisprudencia);
    setShowJurisprudenciaDetalhes(true);
  };

  const handleCloseJurisprudenciaDetails = () => {
    setShowJurisprudenciaDetalhes(false);
    limparJurisprudenciaSelecionada();
  };

  const handleRemoveJurisprudencia = async (idJurisprudencia) => {
    try {
      await removerJurisprudencia(idJurisprudencia);
      alert('Jurisprudência removida com sucesso!');
    } catch (error) {
      console.error('Erro ao remover jurisprudência:', error);
      alert('Erro ao remover jurisprudência');
    }
  };
  
  // ========== HANDLERS DE CHAT ==========
  
  // ✅ NOVO: Handler que decide qual método de envio de mensagem usar
  const handleSendMessage = () => {
    // Verifica se o modelo selecionado suporta a nova funcionalidade
    const modelSupportsStreaming = selectedModel?.id.startsWith('gpt-') || selectedModel?.id.includes('gemini');
    
    // Se o modo streaming estiver ativo e o modelo for compatível, usa a nova função
    if (isStreamingEnabled && modelSupportsStreaming) {
      console.log('🚀 Enviando com STREAM...');
      sendStreamMessage();
    } else {
      // Caso contrário, usa a função padrão (antiga)
      console.log('✉️ Enviando com método PADRÃO...');
      sendMessage();
    }
  };

  // ========== HANDLERS DE DOCUMENTOS ==========

  const handleOpenDocumentDialog = (documento) => {
    selecionarDocumento(documento);
    setShowDocumentDialog(true);
  };

  const handleCloseDocumentDialog = () => {
    setShowDocumentDialog(false);
    limparDocumentoSelecionado();
  };

  const handleGenerateDocument = async (instrucoes = null, nomeDocumento = null) => {
    try {
      await gerarCorpoDocumento(instrucoes, nomeDocumento);
    } catch (error) {
      throw error;
    }
  }

  const handleCopyDocument = async () => {
    try {
      await copiarConteudo();
    } catch (error) {
      throw error;
    }
  };

  const handleDownloadDocument = async (nomeDocumento = null) => {
    try {
      await downloadDocx(nomeDocumento);
      const nomeArquivo = nomeDocumento || documentoSelecionado?.titulo || 'documento';
      alert(`Documento "${nomeArquivo}" baixado com sucesso!`);
    } catch (error) {
      throw error;
    }
  };

  // ========== RENDER ==========

  return (
    <div className="dashboard-container">
      {/* Header */}
      <DashboardHeader
        onToggleSidebar={handleToggleSidebar}
        onBackToSystem={handleBackToSystem}
        debugMode={debugMode}
        onToggleDebug={handleToggleDebug}
      />

      {/* Sidebar com lista de casos */}
      <CaseSidebar
        casos={casos}
        casoSelecionado={casoSelecionado}
        isOpen={showMenuSanduiche}
        loading={loadingCasos}
        onSelectCaso={handleSelectCaso}
        onClose={() => setShowMenuSanduiche(false)}
        onCreateNewCase={handleOpenNewCaseDialog}
      />

      {/* Conteúdo Principal */}
      <div className="dashboard-content">
        {/* Painel Esquerdo (30%) - Informações do Caso */}
        <CasePanel
          casoSelecionado={casoSelecionado}
          jurisprudencias={jurisprudenciasRelacionadas}
          loadingJurisprudencias={loadingJurisprudencias}
          onViewJurisprudenciaDetails={handleViewJurisprudenciaDetails}
          onRemoveJurisprudencia={handleRemoveJurisprudencia}
          onGenerateDocument={handleOpenDocumentDialog}
          debugMode={debugMode}
        />

        {/* Painel Direito (70%) - Chat */}
        <ChatPanel
          models={models}
          selectedModel={selectedModel}
          messages={messages}
          inputMessage={inputMessage}
          isLoading={isChatLoading}
          messagesEndRef={messagesEndRef}
          onSelectModel={selectModel}
          onUpdateInput={updateInputMessage}
          onSendMessage={handleSendMessage} // ✅ ALTERADO: Passa o novo handler para o ChatPanel
        />
      </div>

      {/* Dialog: Novo Caso */}
      <NewCaseDialog
        isOpen={showNovoCasoDialog}
        onClose={handleCloseNewCaseDialog}
        onCreate={handleCreateCase}
        loading={criandoCaso}
      />

      {/* Dialog: Detalhes da Jurisprudência */}
      <JurisprudenceDetailsDialog
        isOpen={showJurisprudenciaDetalhes}
        jurisprudencia={jurisprudenciaSelecionada}
        onClose={handleCloseJurisprudenciaDetails}
      />

      {/* Dialog: Gerar Documento */}
      <GenerateDocumentDialog
        isOpen={showDocumentDialog}
        documento={documentoSelecionado}
        instrucoesAdicionais={instrucoesAdicionais}
        conteudoGerado={conteudoGerado}
        conteudoDisponivel={conteudoGeradoDisponivel}
        gerando={gerandoCorpoDocumento}
        baixando={baixandoDocumento}
        onClose={handleCloseDocumentDialog}
        onUpdateInstrucoes={atualizarInstrucoesAdicionais}
        onGenerate={handleGenerateDocument}
        onCopy={handleCopyDocument}
        onDownload={handleDownloadDocument}
      />
    </div>
  );
};

export default Dashboard;